import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useParams } from "react-router-dom";
import Select from "react-select";

import ActionButton from "../formInputs/buttons/ActionButton";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import RatingTable from "../display/RatingTable";
import useGroupData from "../../hooks/useGroupData";

import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/BatchStudentRatings.scss";

/**
 * BatchStudentRatings is responsible for rendering the rating input interface
 * for multiple students in a selected grading profile (e.g. a homeroom class or group).
 * It allows users to:
 * - Select a grade-level EPR (Elementary Progress Report) profile
 * - Select a subject/template
 * - Load existing content ratings
 * - Input and edit ratings for each student using the RatingTable
 *
 * @component
 * @param {Object} props
 * @param {Object} props.state - Global state containing selectedEpr and gradingProfiles.
 * @param {Function} props.dispatch - Function to update the global EPR-related state.
 */
const BatchStudentRatings = ({ dispatch, state }) => {
    const { state: locationState, pathname } = useLocation();
    const { gradingProfileId } = useParams();

    const { gradingProfiles, navCollapsed, selectedEpr } = state;

    const { state: globalState } = useContext(GlobalContext);
    const { currentTimePeriodDto, schoolLevelTemplateDto, schoolYearDto, userDetails, token } = globalState || {};

    const [hasChanges, setHasChanges] = useState(false);
    const [selectedSubjectId, setSelectedSubjectId] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [subjectOptions, setSubjectOptions] = useState([]);
    const [existingRatingsData, setExistingRatingsData] = useState([]);
    const [existingStudentComments, setExistingStudentComments] = useState([]);
    const [view, setView] = useState("");
    const [groupOptions, setGroupOptions] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedGroupContent, setSelectedGroupContent] = useState([]);

    const ratingTableRef = useRef();
    const shouldShowEprSelect = !gradingProfileId && gradingProfiles?.length > 1;
    const schoolLevelForCurrent =
        currentTimePeriodDto?.name === "1st Semester"
            ? schoolLevelTemplateDto?.firstSemester
            : schoolLevelTemplateDto?.secondSemester;

    const gradeObjects = schoolLevelForCurrent?.gradeToSubjects;

    const { fetchAllGroupOptions, fetchSelectedGroupInfo, fetchSelectedGroupStudentComments } = useGroupData({
        selectedGroups,
        selectedEpr,
        selectedTemplate,
        setExistingStudentComments,
        setGroupOptions,
        setSelectedGroupContent,
        token
    });

    const handleEprChange = (profile) => {
        if (hasChanges && ratingTableRef.current?.submitChanges) {
            ratingTableRef.current.submitChanges();
        }

        dispatch({ type: "SET_SELECTED_EPR", payload: profile });
        setSelectedTemplate(null);
        setSelectedSubjectId(null);
    };

    const handleSubjectChange = (e) => {
        if (hasChanges && ratingTableRef.current?.submitChanges) {
            ratingTableRef.current.submitChanges();
        }
        const templateId = parseInt(e.target.value);
        const subject = subjectOptions.find((s) => s.templateId === templateId);
        setSelectedTemplate(subject);
        setSelectedSubjectId(templateId);
    };

    const fetchRatings = useCallback(() => {
        if (!selectedEpr || !selectedTemplate || !token) return;

        const gradingCriteria = selectedEpr.gradingCriteriaList.find(
            (criteria) => parseInt(criteria.parentTemplate, 10) === selectedTemplate.templateId
        );
        if (!gradingCriteria) return;

        const options = {
            action: "getContentRatings",
            gradingProfileId: selectedEpr.key,
            gradingCriteriaId: gradingCriteria.key,
            token
        };
        const commentOptions = {
            action: "getStudentCriteriaComments",
            gradingCriteriaId: gradingCriteria.key,
            token
        };

        ElementaryProgressReportDao(options).then((response) => {
            if (response?.data?.payload) {
                setExistingRatingsData(response.data.payload);
            }
        });
        ElementaryProgressReportDao(commentOptions).then((response) => {
            if (response?.data?.payload) {
                setExistingStudentComments(response.data.payload);
            }
        });
    }, [selectedEpr, selectedTemplate, token]);

    /**
     * Updates the subject dropdown options based on the selected EPR profile.
     * This depends on the semester (1st/2nd) and the grade-to-subject mapping provided
     * by the current school level template.
     */
    useEffect(() => {
        if (selectedEpr?.key && gradeObjects?.length > 0) {
            const gradeMatch = gradeObjects.find((grade) => grade.profileGradeId === selectedEpr.grade);
            setSubjectOptions(gradeMatch?.subjectToStandards || []);
        }
    }, [selectedEpr, gradeObjects]);

    /**
     * When a subject and grading profile are selected, fetch the existing content ratings
     * from the backend and populate the table with those ratings.
     */
    useEffect(() => {
        if (selectedSubjectId && selectedTemplate && selectedEpr) {
            fetchRatings();
        }
    }, [fetchRatings, selectedEpr, selectedSubjectId, selectedTemplate, token]);

    /**
     * If a subjectId was passed via router state (e.g., from another screen),
     * auto-select that subject when subjectOptions are available.
     */
    useEffect(() => {
        if (!selectedSubjectId && locationState?.subjectId && subjectOptions.length > 0) {
            const defaultSubject = subjectOptions.find((s) => s.templateId === locationState.subjectId);
            if (defaultSubject) {
                setSelectedSubjectId(defaultSubject.templateId);
                setSelectedTemplate(defaultSubject);
            }
        }
    }, [locationState, subjectOptions, selectedSubjectId]);

    /**
     * Automatically selects an EPR based on the URL parameter `gradingProfileId`
     * or defaults to the only available EPR if there's exactly one.
     *
     * Ensures the app starts in a usable state with an EPR pre-selected.
     */
    useEffect(() => {
        if (!selectedEpr && gradingProfiles.length > 0 && gradingProfileId) {
            const profileToSet = gradingProfiles.find((p) => p.key === gradingProfileId);
            if (profileToSet) {
                dispatch({ type: "SET_SELECTED_EPR", payload: profileToSet });
            }
        }
        if (!selectedEpr && gradingProfiles.length === 1) {
            dispatch({ type: "SET_SELECTED_EPR", payload: gradingProfiles[0] });
        }
    }, [gradingProfileId, gradingProfiles, selectedEpr, dispatch]);

    /**
     * Updates the current view mode based on the URL path.
     * If the path contains \"groups\", switch to GROUPS view;
     * otherwise default to HOMEROOM view and clear group-related data.
     */
    useEffect(() => {
        if (pathname) {
            if (pathname.includes("groups")) {
                setView("GROUPS");
            } else {
                setView("HOMEROOM");
                setSelectedGroupContent([]);
                setSelectedGroups([]);
            }
        }
    }, [pathname, setSelectedGroupContent, setSelectedGroups, setView]);

    /**
     * When entering GROUPS mode, fetch all available group options
     * for the selected EPR and subject template.
     */
    useEffect(() => {
        if (view === "GROUPS") {
            fetchAllGroupOptions();
        }
    }, [view, fetchAllGroupOptions]);

    const customStyles = {
        container: (provided) => ({
            ...provided,
            width: "68%",
            zIndex: 1
        }),
        control: (base) => ({
            ...base,

            marginLeft: "0.5rem",
            fontSize: "1.15rem",
            fontWeight: "500",
            border: "2px solid #0A4064",
            borderRadius: "8px",
            backgroundColor: "white",
            color: "#0A4064"
        }),
        groupHeading: (base) => ({
            ...base,
            fontWeight: "bold",
            fontSize: "0.9rem",
            color: "#333"
        })
    };

    return (
        <div className="right-side-wrapper" style={navCollapsed ? { maxWidth: "95%" } : { maxWidth: "80%" }}>
            <div className="right-page-title">
                <h4>
                    Input Ratings{" "}
                    {selectedEpr && view === "HOMEROOM" && (
                        <span>
                            &ndash;{" "}
                            {selectedEpr.gradeName === "K"
                                ? "Kindergarten"
                                : `${selectedEpr.gradeName} Grade Homeroom EPR`}
                        </span>
                    )}
                    {view === "GROUPS" && <span>For Group(s)</span>}
                </h4>
                <h5>
                    <span style={{ fontWeight: "700" }}>{currentTimePeriodDto?.name}</span> | {schoolYearDto.name}
                </h5>
            </div>
            <hr />
            {shouldShowEprSelect && (
                <section className="epr-selector-ratings">
                    <h5>Select a Grade:</h5>
                    {gradingProfiles?.length > 1 && (
                        <>
                            <label className="visually-hidden" htmlFor="selectGradeShares">
                                Select a Grade
                            </label>
                            <select
                                className="rating-select-dropdown"
                                id="selectGradeShares"
                                value={selectedEpr?.key || ""}
                                onChange={(e) => {
                                    const selected = gradingProfiles.find(
                                        (p) => p.key.toString() === e.target.value.toString()
                                    );
                                    handleEprChange(selected);
                                }}
                            >
                                <option value="" disabled>
                                    Select Grade
                                </option>
                                {gradingProfiles
                                    .sort((a, b) => a.grade - b.grade)
                                    .map((profile) => (
                                        <option key={profile.key} value={profile.key}>
                                            {profile.gradeName === "K" ? "Kindergarten" : `${profile.gradeName} Grade`}
                                        </option>
                                    ))}
                            </select>
                        </>
                    )}
                </section>
            )}
            {selectedEpr && (
                <div className="subject-dropdown-wrapper">
                    <section className="epr-selector-ratings">
                        <h5>Select a Subject:</h5>
                        <label className="visually-hidden" htmlFor="subjectDropdown">
                            Select a Subject
                        </label>
                        <select
                            id="subjectDropdown"
                            className="rating-select-dropdown"
                            onChange={handleSubjectChange}
                            value={selectedSubjectId || ""}
                        >
                            <option value="" disabled>
                                Select a Subject
                            </option>
                            {subjectOptions.map((subject) => (
                                <option key={subject.templateId} value={subject.templateId}>
                                    {subject.subjectName}
                                </option>
                            ))}
                        </select>
                    </section>
                </div>
            )}
            {selectedTemplate && selectedEpr && view === "HOMEROOM" && (
                <RatingTable
                    existingRatingsData={existingRatingsData}
                    existingStudentComments={existingStudentComments}
                    fetchRatings={fetchRatings}
                    hasChanges={hasChanges}
                    mode={view}
                    ref={ratingTableRef}
                    selectedTemplate={selectedTemplate}
                    selectedEpr={selectedEpr}
                    setExistingRatingsData={setExistingRatingsData}
                    setExistingStudentComments={setExistingStudentComments}
                    setHasChanges={setHasChanges}
                    token={token}
                    userDetails={userDetails}
                />
            )}
            {selectedEpr && selectedTemplate && groupOptions && view === "GROUPS" && (
                <section className="group-select-section">
                    <h5>Select Group(s):</h5>
                    <Select
                        isMulti
                        options={groupOptions}
                        value={selectedGroups}
                        onChange={(selected) => setSelectedGroups(selected || [])}
                        placeholder="Select groups..."
                        styles={customStyles}
                    />
                    <ActionButton
                        label="Enter Ratings"
                        onClick={() => {
                            fetchSelectedGroupInfo();
                            fetchSelectedGroupStudentComments();
                        }}
                    />
                </section>
            )}
            {selectedGroupContent && selectedGroupContent.length > 0 && (
                <RatingTable
                    existingRatingsData={existingRatingsData}
                    existingStudentComments={existingStudentComments}
                    fetchRatings={fetchRatings}
                    hasChanges={hasChanges}
                    mode={view}
                    ref={ratingTableRef}
                    selectedTemplate={selectedTemplate}
                    selectedEpr={selectedEpr}
                    setExistingRatingsData={setExistingRatingsData}
                    setExistingStudentComments={setExistingStudentComments}
                    setHasChanges={setHasChanges}
                    token={token}
                    userDetails={userDetails}
                    groupData={selectedGroupContent}
                    fetchSelectedGroupInfo={fetchSelectedGroupInfo}
                />
            )}
        </div>
    );
};

BatchStudentRatings.propTypes = {
    state: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired
};

export default BatchStudentRatings;
